/**
 * Client incidents (the Webcare-style incident register). One record per
 * incident from DATA_START to now; every incidents payload is a filter over
 * this list so the tiles, charts, CSV exports and age-band downloads agree.
 */
import { Quarter, now, ymd } from '../../clock';
import { rng } from '../../rng';
import { SITE_NAMES, activeClients } from '../../world';
import { Scope, careStaff, trendScopeOf, dateIn, daysBetween, daysInMonth, historyMonths, inScope, memo, pickClient, scopeOf, syncedAt } from './common';

export interface IncidentRecord {
    webcare_id: number;
    date: Date;
    level: string;
    type: string;
    location: string;
    title: string;
    participant: string;
    staff_involved: string;
    choking: boolean;
    open: boolean;
    status_tl: string;
    status_scc: string;
    what_before: string;
    what_during: string;
    what_after: string;
}

const LEVELS: [string, number][] = [
    ['Level 1', 0.6],
    ['Level 1R', 0.8],
    ['Level 2', 6.5],
    ['Level 2R', 1.4],
    ['Level 3', 78],
    ['Level 4', 7.7],
    ['Level 5', 5],
];

/** Incident types, adapted to remote community service delivery. */
const TYPES: [string, number][] = [
    ['Behaviours of concern', 15],
    ['Medical (non-critical)', 13],
    ['Verbal abuse/threats', 10],
    ['Client fall', 9],
    ['Missed/Refused medication', 8],
    ['Missed service – road or weather access', 7],
    ['Error/incorrect medication recording', 6],
    ['Near miss', 6],
    ['No harm or disruption', 5],
    ['Assault/physical aggression', 5],
    ['Vehicle incident', 4],
    ['Property damage', 4],
    ['Heat-related illness', 3],
    ['Dog bite', 3],
    ['Leaving without supervision', 3],
    ['Self-harm or risk of self-harm', 2],
    ['Verbal abuse/threats; Property damage', 2],
];

const TITLES: Record<string, string[]> = {
    'Behaviours of concern': ['Escalated behaviour during personal care', 'Distress after family visit', 'Refused to get on the bus to day program'],
    'Medical (non-critical)': ['High blood sugar reading', 'Chest tightness – clinic review', 'Skin sore needing dressing', 'Ear infection symptoms'],
    'Verbal abuse/threats': ['Verbal threats toward support worker', 'Shouting at staff during meal', 'Argument with visitor at the centre'],
    'Client fall': ['Fall in bathroom', 'Trip on uneven ground outside house', 'Slipped getting out of troopy'],
    'Missed/Refused medication': ['Refused evening medication', 'Morning medication missed – client out bush', 'Declined Webster-pak dose'],
    'Missed service – road or weather access': ['Visit cancelled – road closed after rain', 'Flight cancelled – service missed', 'Creek crossing flooded'],
    'Error/incorrect medication recording': ['Dose not signed on chart', 'Wrong time recorded on chart', 'Chart and pack did not match'],
    'Near miss': ['Gas bottle left on at the centre', 'Hot water urn left within reach', 'Loose step reported'],
    'No harm or disruption': ['Client unsettled overnight', 'Client went for a walk without telling staff'],
    'Assault/physical aggression': ['Client pushed support worker', 'Client struck another client'],
    'Vehicle incident': ['Tyre blowout on Lasseter Highway', 'Troopy bogged on community road', 'Kangaroo strike – minor damage'],
    'Property damage': ['Window broken at respite house', 'Damage to centre door'],
    'Heat-related illness': ['Client dizzy after outing in heat', 'Dehydration symptoms during heatwave'],
    'Dog bite': ['Camp dog bite on leg', 'Dog bite during home visit'],
    'Leaving without supervision': ['Client left the centre without staff', 'Client walked to family camp alone'],
    'Self-harm or risk of self-harm': ['Statement of self-harm – safety plan actioned', 'Self-harm risk escalated to clinic'],
    'Verbal abuse/threats; Property damage': ['Verbal threats and damage to furniture'],
};

/** Chance an incident of a given age (days) is still open in the register. */
function openChance(age: number): number {
    if (age < 3) return 0.7;
    if (age < 7) return 0.36;
    if (age < 14) return 0.2;
    if (age < 21) return 0.13;
    if (age < 28) return 0.09;
    if (age < 60) return 0.05;
    return 0.014;
}

/** Monthly volume: ~55 a month with a summer peak and gentle noise. */
function monthlyVolume(m: string): number {
    const r = rng(`incidents:volume:${m}`);
    const month = Number(m.slice(5));
    const summer = month === 12 || month === 1 || month === 2 ? 7 : month === 3 || month === 11 ? 3 : 0;
    return Math.max(30, Math.round(r.around(52 + summer, 7)));
}

const allIncidents = memo((): IncidentRecord[] => {
    const n = now();
    const out: IncidentRecord[] = [];
    const clients = activeClients();
    const hot = rng('incidents:hot').sample(clients, 9);
    let id = 11000;
    for (const m of historyMonths()) {
        const r = rng(`incidents:${m}`);
        const days = daysInMonth(m);
        const volume = monthlyVolume(m);
        const monthOfQuarter = (Number(m.slice(5)) - 1) % 3; // Jan/Apr/Jul/Oct = 0
        const chokings = monthOfQuarter === 0 ? 1 : r.weighted([[0, 6], [1, 3], [2, 1]] as [number, number][]);
        const list: Omit<IncidentRecord, 'webcare_id'>[] = [];
        for (let i = 0; i < volume; i++) {
            const choking = i < chokings;
            const day = choking && monthOfQuarter === 0 && i === 0 ? r.int(2, 7) : r.int(1, days);
            const date = dateIn(m, day, r.int(6, 21), r.pick([0, 10, 15, 20, 30, 40, 45, 50]));
            const client = pickClient(r, clients, hot, 0.28);
            const type = choking ? 'Medical (non-critical)' : r.weighted(TYPES);
            const level = choking ? r.pick(['Level 2', 'Level 3']) : r.weighted(LEVELS);
            const staff = r.pick(careStaff(client.program, client.site));
            const location = r.bool(0.04) ? SITE_NAMES[0] : client.site;
            const title = choking ? 'Choking episode at mealtime' : r.pick(TITLES[type] || ['Incident reported']);
            list.push({
                date,
                level,
                type,
                location,
                title,
                participant: client.code,
                staff_involved: staff.name,
                choking,
                open: false,
                status_tl: 'Closed',
                status_scc: 'Closed',
                what_before: choking ? 'Client was eating lunch at the aged care centre.' : 'Client was in usual routine with support worker present.',
                what_during: choking ? 'Client coughed and had trouble breathing; staff gave back blows and the food cleared.' : `${title}. Staff responded per the incident procedure.`,
                what_after: choking ? 'Clinic nurse reviewed; speech pathology referral made for a mealtime plan.' : 'Family and team leader informed; follow-up noted in the client file.',
            });
        }
        list.sort((a, b) => a.date.getTime() - b.date.getTime());
        for (const inc of list) {
            const rec = { ...inc, webcare_id: id++ };
            if (rec.date.getTime() > n.getTime()) {
                continue;
            }
            const u = rng(`incident:open:${rec.webcare_id}`).next();
            const age = daysBetween(rec.date, n);
            rec.open = u < openChance(age);
            if (rec.open) {
                rec.status_scc = 'Open';
                rec.status_tl = age < 5 || u < openChance(age) / 2 ? 'Open' : 'Closed';
            }
            out.push(rec);
        }
    }
    return out;
});

export function incidentRecords(): IncidentRecord[] {
    return allIncidents('all');
}

export function incidentsIn(s: Scope): IncidentRecord[] {
    return incidentRecords().filter(i => inScope(i.date, s));
}

function ageOf(i: IncidentRecord): number {
    return daysBetween(i.date, now());
}

export function incidentRow(i: IncidentRecord): Record<string, unknown> {
    return {
        webcare_id: i.webcare_id,
        incident_date: ymd(i.date),
        level: i.level,
        type: i.type,
        location: i.location,
        title: i.title,
        participant: i.participant,
        staff_involved: i.staff_involved,
        status_tl: i.status_tl,
        status_scc: i.status_scc,
    };
}

/** GET /api/incidents/stats (board twin returns the same keys). */
export function incidentStats(q: Quarter | null, board = false): Record<string, unknown> {
    void board;
    const s = scopeOf(q);
    const list = incidentsIn(s);
    const total = list.length;
    const open = list.filter(i => i.open);
    const band = (min: number, max: number) => open.filter(i => ageOf(i) >= min && ageOf(i) < max).length;
    const overdue28 = open.filter(i => ageOf(i) >= 28).length;
    const weekAgo = now().getTime() - 7 * 86400000;

    const levels = new Map<string, number>();
    const types = new Map<string, number>();
    const locations = new Map<string, number>(SITE_NAMES.map(n => [n, 0]));
    for (const i of list) {
        levels.set(i.level, (levels.get(i.level) || 0) + 1);
        types.set(i.type, (types.get(i.type) || 0) + 1);
        locations.set(i.location, (locations.get(i.location) || 0) + 1);
    }
    const ts = trendScopeOf(q);
    const months = new Map<string, number>(ts.months.map(m => [m, 0]));
    for (const i of incidentsIn(ts)) {
        const m = ymd(i.date).slice(0, 7);
        months.set(m, (months.get(m) || 0) + 1);
    }
    const trendMonths = [...months.keys()].sort();

    return {
        total,
        recent_7d: list.filter(i => i.date.getTime() >= weekAgo).length,
        open: open.length,
        closed: total - open.length,
        closure_rate: total ? Math.round(((total - open.length) / total) * 100) : 0,
        overdue: overdue28,
        overdue_14: band(14, 21),
        overdue_21: band(21, 28),
        overdue_28: overdue28,
        choking: list.filter(i => i.choking).length,
        retired: total ? rng(`incidents:retired:${q?.key ?? 'all'}`).int(0, q ? 2 : 4) : 0,
        by_level: [...levels.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([level, count]) => ({ level, count })),
        by_type: [...types.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([type, count]) => ({ type, count })),
        by_location: [...locations.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([location, count]) => ({ location, count })),
        monthly_trends: trendMonths.map(month => ({ month, count: months.get(month) || 0 })),
        last_synced_at: syncedAt('incidents'),
    };
}

/** GET /api/incidents/choking — rows.length always equals stats.choking. */
export function incidentChoking(q: Quarter | null): Record<string, unknown> {
    const rows = incidentsIn(scopeOf(q)).filter(i => i.choking).map(incidentRow);
    return { quarter: q?.key ?? null, count: rows.length, rows };
}

/** GET /api/incidents/overdue?days=14|21|28 — open incidents in that exclusive age band. */
export function incidentOverdue(days: 14 | 21 | 28, q: Quarter | null): Record<string, unknown> {
    const [min, max] = days === 14 ? [14, 21] : days === 21 ? [21, 28] : [28, Infinity];
    const rows = incidentsIn(scopeOf(q))
        .filter(i => i.open && ageOf(i) >= min && ageOf(i) < max)
        .map(incidentRow);
    return { days, quarter: q?.key ?? null, count: rows.length, rows };
}

/** GET /api/incidents/export — every incident in scope with the narrative columns. */
export function incidentExport(q: Quarter | null): Record<string, unknown> {
    const rows = incidentsIn(scopeOf(q)).map(i => ({
        ...incidentRow(i),
        what_before: i.what_before,
        what_during: i.what_during,
        what_after: i.what_after,
    }));
    return { quarter: q?.key ?? null, count: rows.length, rows };
}

/** Reportable incidents (levels ending in "R") for the CEO report. */
export function incidentReportable(q: Quarter | null): { count: number; byLevel: Record<string, number> } {
    const list = incidentsIn(scopeOf(q)).filter(i => /R$/.test(i.level));
    const byLevel: Record<string, number> = {};
    for (const i of list) {
        byLevel[i.level] = (byLevel[i.level] || 0) + 1;
    }
    return { count: list.length, byLevel };
}
